<script lang="ts">
  /* The first filter row of every catalogue screen (2026-09-13 feedback).

     Products, «Доступ позиций», the Prices tabs and Склад are one database
     looked at four ways, so they are narrowed the same way, in the same order:
     search by name / SKU / EAN, category, subcategory, supplier — and
     «Настройка формы экрана» at the end. What only one screen has (the
     minimum and the reorder on Склад, the client pickers on Prices) goes on a
     second row under it, `options`, never in between.

     The values belong to the page (bindable): Products and Склад filter on the
     server, the matrices in the browser. Remembering them across screens is
     $lib/catalogFilters. A picker with nothing to pick stays in its place,
     disabled, so the row reads the same on every screen.

     2026-09-19 (owner): the shared row sits on a tinted band of its own. On a
     phone both rows wrap into a column of pickers, and without the band the
     shared filter ran straight into the view's own options under it. */
  import type { Snippet } from "svelte";
  import { _ } from "svelte-i18n";
  import SearchSelect from "$components/SearchSelect.svelte";
  import type { Category } from "$lib/catalog";

  export type FilterChange = "q" | "category" | "subcategory" | "supplier";

  let {
    q = $bindable(""),
    category = $bindable(null),
    subcategory = $bindable(null),
    supplier = $bindable(""),
    categories = [],
    suppliers = [],
    searchPlaceholder = "",
    oncolumns,
    onchange,
    options,
    actions,
    sticky = false,
  }: {
    q?: string;
    category?: number | null;
    subcategory?: number | null;
    supplier?: string;
    categories?: Category[];
    suppliers?: string[];
    /** Склад also matches batch numbers and says so. */
    searchPlaceholder?: string;
    oncolumns?: () => void;
    /** After the bound value has changed; the page reloads or refilters. */
    onchange?: (what: FilterChange) => void;
    /** The screen's own second row. */
    options?: Snippet;
    /** Page-level buttons, pinned to the right end of the first row — a
        «Сохранить изменения» for the whole sheet rather than one per line
        (owner, 2026-09-16). */
    actions?: Snippet;
    /** Pin the row to the top of the scrolling area, so a screen that edits
        in place keeps its Save reachable however far down the table runs. */
    sticky?: boolean;
  } = $props();

  const parents = $derived(categories.filter((c) => c.parent_id == null));
  const subs = $derived(category == null ? [] : categories.filter((c) => c.parent_id === category));
  const supplierOptions = $derived(suppliers.map((s) => ({ value: s, label: s })));
  const placeholder = $derived(searchPlaceholder || $_("pl.filter.search.placeholder"));
</script>

<div class="cfb" class:cfb--sticky={sticky}>
  <div class="cfb-row cfb-row--shared">
    <label class="cfb-search">
      <span class="material-icons-outlined" aria-hidden="true">search</span>
      <input
        type="text"
        bind:value={q}
        {placeholder}
        aria-label={placeholder}
        oninput={() => onchange?.("q")}
      />
    </label>
    <select
      class="cfb-select"
      bind:value={category}
      disabled={parents.length === 0}
      aria-label={$_("pl.filter.categories")}
      onchange={() => {
        // A subcategory belongs to its category.
        subcategory = null;
        onchange?.("category");
      }}
    >
      <option value={null}>{$_("pl.filter.allCategories")}</option>
      {#each parents as c (c.id)}<option value={c.id}>{c.name}</option>{/each}
    </select>
    <select
      class="cfb-select"
      bind:value={subcategory}
      disabled={subs.length === 0}
      aria-label={$_("pl.filter.subcategories")}
      onchange={() => onchange?.("subcategory")}
    >
      <option value={null}>{$_("pl.filter.allSubcategories")}</option>
      {#each subs as c (c.id)}<option value={c.id}>{c.name}</option>{/each}
    </select>
    <SearchSelect
      options={supplierOptions}
      bind:value={supplier}
      allLabel={$_("pl.filter.allSuppliers")}
      searchPlaceholder={$_("pl.filter.supplierSearch")}
      disabled={suppliers.length === 0}
      onchange={() => onchange?.("supplier")}
    />
    {#if oncolumns}
      <button type="button" class="cfb-btn" onclick={oncolumns} title={$_("pl.columns.title")}>
        <span class="material-icons-outlined" aria-hidden="true">tune</span>
        {$_("pl.columns.title")}
      </button>
    {/if}
    {#if actions}
      <div class="cfb-actions">{@render actions()}</div>
    {/if}
  </div>
  {#if options}
    <div class="cfb-row cfb-row--options">{@render options()}</div>
  {/if}
</div>

<style>
  .cfb { display: flex; flex-direction: column; gap: 10px; }
  /* `.content` in AppShell is the scrolling box, so top: 0 pins the row just
     under the topbar; the background keeps the table from showing through. */
  .cfb--sticky {
    position: sticky; top: 0; z-index: 8;
    background: var(--color-bg); padding: 6px 0;
  }
  /* Pushed to the far right of the row, whatever is in front of it. */
  .cfb-actions { margin-left: auto; display: flex; align-items: center; gap: 8px; }
  .cfb-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  .cfb-row--shared {
    padding: 8px; background: var(--cat-filter-bg); border: 1px solid var(--cat-filter-line); border-radius: 8px;
  }
  .cfb-search {
    display: flex; align-items: center; gap: 6px; flex: 1 1 220px; max-width: 300px;
    padding: 0 10px; background: #fff; border: 1px solid var(--color-divider); border-radius: 6px;
  }
  .cfb-search:focus-within { border-color: var(--color-primary); }
  .cfb-search input {
    flex: 1; min-width: 0; padding: 7px 0; border: none; outline: none;
    font-family: inherit; font-size: 13px; background: none;
  }
  .cfb-search .material-icons-outlined { font-size: 18px; color: var(--color-text-muted); }
  .cfb-select {
    padding: 7px 10px; max-width: 200px; background: #fff;
    border: 1px solid var(--color-divider); border-radius: 6px; font-family: inherit; font-size: 13px;
  }
  .cfb-select:disabled { opacity: 0.5; }
  .cfb-btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px;
    background: #fff; border: 1px solid var(--color-divider); border-radius: 6px;
    font-family: inherit; font-size: 13px; color: var(--color-text); cursor: pointer; white-space: nowrap;
  }
  .cfb-btn:hover { border-color: var(--color-primary); color: var(--color-primary); }
  .cfb-btn .material-icons-outlined { font-size: 18px; }

  @media (max-width: 700px) {
    .cfb-search { flex-basis: 100%; max-width: none; }
    .cfb-select { flex: 1 1 140px; max-width: none; }
  }
</style>
